// frontend/src/pages/PurchaseOrderDetail.jsx
//
// One purchase order, from draft to its terminal state. The header offers
// exactly the actions the PO's current status allows
// (components/purchase-orders/poActions.js — one primary button, a Download
// menu and a More actions menu; the PO list offers the same per row). A draft
// also carries the store's Show prices switch there, since the page, the
// lines and the PDF show prices only where the store opted in. The
// lines grid shows what each line has ordered / received / outstanding.
//
// Receiving happens IN that grid, and the page keeps one shape throughout:
// while goods are still owed, the Received column is editable; typing a new
// received quantity marks the line pending (the cell tints, Remaining
// follows) and the header switches to Submit receipt / Discard changes.
// Submit asks for one confirmation and books the differences as one goods
// receipt, dated the store's today; the grid then shows the new booked
// quantities in place. "Receive" is a shortcut that fills every open
// line to its ordered quantity — the usual delivery is the whole order — so
// the merchant corrects the short lines and submits.
//
// Route: /purchase-orders/:id/:mode? — a record route (tabs/routeRegistry.jsx);
// the tab title is the PO number once it loads. `/receive` (the Home and list
// "Receive" buttons) opens the page with that shortcut already applied;
// hooks/useOpenPurchaseOrder keeps one tab per PO across both spellings.

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTabParams } from '../tabs/useTabParams';
import { useTranslation } from 'react-i18next';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import {
  Button,
  Alert,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { ICON, CaretDown, X } from '../icons';
import StatusChip from '../components/common/StatusChip';
import { PoStatusBanner, PoStatusCell, PoStatusChip } from '../components/grid/StatusChipCells';
import { useIsShopify, itemIdentityColumns } from '../components/grid/productIdentity';
import { formatItemLabel } from '../utils/itemLabel';
import { buildPinnedTotalsRow, isPinnedTotalRow, sumFields, wrapColumnsForPinnedTotals } from '../utils/pinnedTotals';
import Tile from '../components/layout/Tile';
import { useAgGridTheme } from '../utils/agGridTheme';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, flexColumn } from '../utils/gridDefaults';
import { useFormatters } from '../hooks/useFormatters';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useIssueDate, usePurchaseOrder, useReceiptHistory } from '../hooks/usePOData';
import {
  useReceiveGoods, useProjectPO, useReschedulePO, useUpdatePONotes, useShowDraftPrices,
} from '../hooks/usePOMutations';
import AppDatePicker from '../components/common/AppDatePicker';
import FieldLine from '../components/common/FieldLine';
import { useStoreToday } from '../hooks/useStoreToday';
import { canReschedule } from '../components/purchase-orders/poActions';
import { translateRequestError } from '../queries/api';
import { useTabs } from '../tabs/useTabs';
import { useWriteGate } from '../hooks/useWriteGate';
import PurchaseOrderActions from '../components/purchase-orders/PurchaseOrderActions';
import { ProjectionPanel } from '../components/purchase-orders/ChannelProjection';
import { PROJECTION_SYNCED } from '../utils/channelProjection';
import { usePurchaseOrderLifecycle } from '../components/purchase-orders/usePurchaseOrderLifecycle';
import { poShowsReceipts } from '../components/purchase-orders/poReceiptColumns';
import '../styles/pages.css';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

// Per-line aggregable fields for the pinned totals row. Cost is per-unit and
// not summed. `booked` is summed so the Received total tints like a line
// while a receipt is pending.
const TOTAL_FIELDS = ['Quantity', 'QuantityReceived', 'outstanding', 'booked'];

const PURCHASE_ORDERS_PATH = '/purchase-orders';

const ISSUED_STATUSES = new Set(['open', 'partially_received', 'received', 'closed']);

function PurchaseOrderDetail() {
  // Tab-scoped params: the id of THIS tab's PO, whichever tab is in front.
  const { id, mode } = useTabParams();
  const openedInReceiveMode = mode === 'receive';
  const { t } = useTranslation();
  const columnTypes = useColumnTypes();
  const gridState = useGridState('purchase_order_detail_lines');
  const { formatNumber, formatCurrency, formatDate, formatDateTime, parseNumber } = useFormatters();
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const { confirm, dialogProps } = useConfirmDialog();
  const { setOwnTitle, replaceOwnTab } = useTabs();
  // A store whose ERP owns the PO lifecycle creates its orders there: no
  // Duplicate here, the same gate as the recommendations' Create PO.
  const mayCreateOrders = useWriteGate('po_final').mayWrite;

  const { data: poData, loading, errorObject: poError } = usePurchaseOrder(id);
  const historyQuery = useReceiptHistory(id);
  const receiptHistory = historyQuery.data ?? [];
  // The receipt history sits collapsed — the grid already shows what was
  // received — until the merchant opens it, or until a receipt has failed to
  // reach Shopify: once the toast is gone, that signal lives only there.
  const [historyOpen, setHistoryOpen] = useState(null);
  const historyFailed = receiptHistory.some((r) => r.shopify_sync_status === 'failed');

  // Every PO carries its supplier currency (PurchaseOrderHeader.CurrencyCode
  // is non-null, PROTECT); `poData` alone may still be loading.
  const poCurrency = poData?.currency.currency_code;

  const receiveMutation = useReceiveGoods();
  const { mutateAsync: projectPO } = useProjectPO();
  const notesMutation = useUpdatePONotes();
  const rescheduleMutation = useReschedulePO();
  const storeToday = useStoreToday();
  const showDraftPrices = useShowDraftPrices();
  const submitting = receiveMutation.isPending;

  // The receipt being typed: line id → the Received quantity the merchant
  // entered, as typed. `rowData` bounds it to [booked, ordered] and shows it
  // in place of the booked quantity; Submit receipt books the differences.
  // An entry that bounds to the booked quantity is inert (no tint, nothing
  // to submit).
  const [pending, setPending] = useState({});
  // The internal note as typed; null = untouched, so the field tracks the
  // saved note (including its re-read after a save).
  const [notesEdit, setNotesEdit] = useState(null);
  // The expected delivery date as picked; null = untouched, like the note.
  const [arrivalEdit, setArrivalEdit] = useState(null);
  // The field holds no day that can be saved (unfinished, or before today).
  const [arrivalRefused, setArrivalRefused] = useState(false);
  // A draft's picked delivery date; null = the one issuing would set. A draft
  // stores no date, so it is not saved: Issue starts its confirmation at it.
  const [issuePick, setIssuePick] = useState(null);


  const getRowId = useCallback((params) => String(params.data.PurchaseOrderLineID), []);

  useEffect(() => {
    if (poData?.po_number) setOwnTitle(poData.po_number);
  }, [poData?.po_number, setOwnTitle]);

  const status = poData?.status;
  const isIssued = ISSUED_STATUSES.has(status);
  // A draft stores no delivery date; its page shows the one issuing it now
  // would set.
  const issueDate = useIssueDate(id, { enabled: status === 'draft' });
  // No columns until the PO has loaded; the grid is not shown before then.
  const showsReceipts = poData ? poShowsReceipts(status) : false;
  // Received is typed straight into the grid while the supplier still owes goods.
  const receivable = status === 'open' || status === 'partially_received';

  // Every line, with the pending received quantity (if any) in place of the
  // booked one and `outstanding` following it; `booked` keeps the truth. The
  // one home of the bound: floor = booked (a receipt is never taken back
  // here, and a quantity typed before someone else's receipt landed cannot
  // undercut it), ceiling = ordered (the endpoint refuses over-receipt).
  const rowData = useMemo(() => {
    if (!poData?.order_lines) return [];
    return poData.order_lines.map((line) => {
      const typed = pending[line.PurchaseOrderLineID] ?? line.QuantityReceived;
      const received = Math.min(Math.max(typed, line.QuantityReceived), line.Quantity);
      return {
        ...line,
        booked: line.QuantityReceived,
        QuantityReceived: received,
        outstanding: line.Quantity - received,
      };
    });
  }, [poData, pending]);

  const pinnedBottomRowData = useMemo(() => {
    const totals = sumFields(rowData, TOTAL_FIELDS);
    return buildPinnedTotalsRow(totals, 'item_code', t('grid.totals.label'));
  }, [rowData, t]);

  // What the pending quantities add up to — named in the confirmation and
  // booked by Submit receipt.
  const receiptSummary = useMemo(() => {
    let lines = 0;
    let qty = 0;
    for (const row of rowData) {
      const delta = row.QuantityReceived - row.booked;
      if (delta <= 0) continue;
      lines += 1;
      qty += delta;
    }
    return { lines, qty };
  }, [rowData]);
  const hasPending = receiptSummary.lines > 0;

  const totalOrdered = useMemo(
    () => (poData?.order_lines ?? []).reduce((sum, l) => sum + l.Quantity, 0),
    [poData],
  );
  const totalReceived = useMemo(
    () => (poData?.order_lines ?? []).reduce((sum, l) => sum + l.QuantityReceived, 0),
    [poData],
  );

  // The receipt being typed tints the Received cell — lines and the totals
  // row alike — while the shown quantity differs from the booked one.
  const gridContext = useMemo(() => ({
    unsavedCellClass: (params) => params.colDef.field === 'QuantityReceived'
      && !!params.data && params.data.QuantityReceived !== params.data.booked,
  }), []);

  const columnDefs = useMemo(() => {
    const identity = itemIdentityColumns(isShopify, {
      productHeader: t('purchaseOrderDetail.columns.product'), product: flexColumn(2),
      codeHeader: t('purchaseOrderDetail.columns.itemCode'),
      skuHeader: t('purchaseOrderDetail.columns.sku'),
      nameHeader: t('purchaseOrderDetail.columns.itemName'), name: flexColumn(),
    });
    // A line still owed takes a received quantity; the totals row never does.
    const lineOwed = (params) => receivable && !isPinnedTotalRow(params)
      && params.data?.Quantity > params.data?.booked;
    const received = {
      field: 'QuantityReceived',
      headerName: t('purchaseOrderDetail.columns.received'),
      type: 'numberColumn',
      editable: lineOwed,
      // Whole units; not a number → the cell keeps its value. The bound is
      // applied where the row is built (`rowData`).
      valueParser: (params) => {
        const parsed = Math.floor(parseNumber(params.newValue));
        return isNaN(parsed) ? params.oldValue : parsed;
      },
    };

    return wrapColumnsForPinnedTotals([
      ...identity,
      {
        field: 'Quantity',
        headerName: t('purchaseOrderDetail.columns.ordered'),
        type: 'numberColumn',
      },
      ...(showsReceipts ? [
        received,
        {
          field: 'outstanding',
          headerName: t('purchaseOrderDetail.columns.remaining'),
          type: 'numberColumn',
        },
      ] : []),
      // The page shows the PO's prices exactly when its PDF prints them
      // (`shows_prices`, the server's rule): a draft hides them until the
      // store opts in, so the merchant sees the document the supplier gets.
      ...(poData?.shows_prices ? [{
        field: 'Cost',
        headerName: `${t('purchaseOrderDetail.columns.unitCost')} (${poCurrency})`,
        type: 'decimalColumn',
        valueFormatter: (p) => formatCurrency(p.value, poCurrency),
      }] : []),
      ...(showsReceipts ? [
        {
          field: 'last_received_date',
          headerName: t('purchaseOrderDetail.columns.lastReceived'),
          type: 'dateColumn',
        },
      ] : []),
      // Every line carries a status in the header's vocabulary, a draft's too.
      {
        field: 'line_status',
        headerName: t('purchaseOrderDetail.columns.lineStatus'),
        type: 'centeredColumn',
        cellRenderer: PoStatusCell,
      },
    ]);
  }, [t, formatCurrency, parseNumber, isShopify, poCurrency, receivable, showsReceipts, poData?.shows_prices]);

  // ── Receiving ────────────────────────────────────────────────────────────

  // The "Receive" shortcut: every line still owed is filled to its
  // ordered quantity.
  const fillOutstanding = useCallback(() => {
    setPending(Object.fromEntries(
      poData.order_lines
        .filter((line) => line.Quantity > line.QuantityReceived)
        .map((line) => [line.PurchaseOrderLineID, line.Quantity]),
    ));
  }, [poData]);

  // A /receive link lands with the shortcut applied, once — a PO that is no
  // longer receivable shows the plain view.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!openedInReceiveMode || prefilled.current || !receivable) return;
    prefilled.current = true;
    fillOutstanding();
  }, [openedInReceiveMode, receivable, fillOutstanding]);

  const handleCellValueChanged = useCallback((params) => {
    if (params.column.colId !== 'QuantityReceived') return;
    const lineId = params.data.PurchaseOrderLineID;
    setPending((prev) => {
      const next = { ...prev };
      if (params.newValue === params.data.booked) delete next[lineId];
      else next[lineId] = params.newValue;
      return next;
    });
  }, []);

  const discardReceipt = useCallback(() => setPending({}), []);

  // The draft's "Show prices" switch writes the store setting; the page
  // re-reads `shows_prices` from the server, so the switch never shows a
  // value the store does not hold.
  const handleShowPrices = useCallback(async (event) => {
    try {
      await showDraftPrices.mutateAsync(event.target.checked);
    } catch (error) {
      console.error('showDraftPrices:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrderDetail.showPricesFailed'), 'error');
    }
  }, [showDraftPrices, t]);

  // One confirmation — units, lines and where they land — then the receipt
  // is booked on the store's today. Nothing else to collect.
  const submitReceipt = useCallback(async () => {
    const ok = await confirm({
      title: t('purchaseOrderDetail.receive.confirmTitle'),
      message: t(
        isShopify
          ? 'purchaseOrderDetail.receive.confirmMessageShopify'
          : 'purchaseOrderDetail.receive.confirmMessage',
        {
          units: t('purchaseOrderDetail.receive.unitCount', {
            count: receiptSummary.qty, qty: formatNumber(receiptSummary.qty, 0),
          }),
          count: receiptSummary.lines,
          location: poData.location_name,
        },
      ),
      confirmLabel: t('common.confirm'),
      variant: 'info',
    });
    if (!ok) return;

    const lines = rowData
      .filter((row) => row.QuantityReceived > row.booked)
      .map((row) => ({
        line_id: row.PurchaseOrderLineID,
        quantity_received: row.QuantityReceived - row.booked,
      }));

    try {
      // Resolves once the PO has been re-read (useReceiveGoods awaits its
      // refetch), so the pending quantities give way to the booked ones —
      // clearing earlier would flash the old values through the grid.
      const data = await receiveMutation.mutateAsync({ id, lines });
      setPending({});

      // The receipt is booked either way (purchase_orders/receiving.py posts
      // it before purchase_orders/channel_sync.py pushes). `failed` is the
      // one outcome the merchant has to act on: Manexys inventory moved,
      // Shopify's did not. An absent key is a contract break that belongs in
      // the catch below.
      if (data.shopify_sync_status === 'failed') {
        setHistoryOpen(true);
        showToast(t('purchaseOrderDetail.receive.shopifySyncFailed'), 'error');
      } else {
        showToast(t('purchaseOrderDetail.receive.success', { poNumber: poData.po_number }), 'success');
      }
    } catch (error) {
      console.error('receiveGoods:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrderDetail.receive.failed'), 'error');
    }
  }, [confirm, t, isShopify, formatNumber, receiptSummary, poData, rowData, id, receiveMutation]);

  // ── Internal note ────────────────────────────────────────────────────────

  const notesDirty = notesEdit !== null && notesEdit !== poData?.notes;

  const saveNotes = useCallback(async () => {
    try {
      // Resolves once the PO has been re-read (useUpdatePONotes awaits it),
      // so the field goes from the typed text straight to the saved one.
      await notesMutation.mutateAsync({ id, notes: notesEdit });
      setNotesEdit(null);
      showToast(t('purchaseOrderDetail.notes.saved'), 'success');
    } catch (error) {
      console.error('updatePONotes:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrderDetail.notes.failed'), 'error');
    }
  }, [notesMutation, id, notesEdit, t]);

  // ── Expected delivery ────────────────────────────────────────────────────

  const arrivalDirty = arrivalEdit !== null && arrivalEdit !== poData?.need_by_date;

  const saveArrival = useCallback(async () => {
    try {
      // Resolves once the PO has been re-read (usePOWrite awaits the store
      // data refresh), so the field goes from the picked day to the saved one.
      await rescheduleMutation.mutateAsync({ id, needByDate: arrivalEdit });
      setArrivalEdit(null);
      showToast(t('purchaseOrders.messages.deliveryDateUpdated'), 'success');
    } catch (error) {
      console.error('reschedulePO:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrders.messages.deliveryDateFailed'), 'error');
    }
  }, [rescheduleMutation, id, arrivalEdit, t]);

  // ── Lifecycle actions ────────────────────────────────────────────────────

  // Receive fills the lines in place; a deleted draft takes this tab with it.
  const { run, running: inFlight, dialogs: lifecycleDialogs } = usePurchaseOrderLifecycle({
    onReceive: fillOutstanding,
    onDeleted: () => replaceOwnTab(PURCHASE_ORDERS_PATH),
  });
  const handleAction = useCallback(
    (action) => run(poData, action, { needByDate: issuePick }),
    [run, poData, issuePick],
  );
  const running = id in inFlight ? inFlight[id] : null;
  const busy = running !== null || submitting;

  // Retry / re-project the Shopify projection. The endpoint answers 200 with
  // the NEW status — a retry that fails again is not an exception, so the
  // outcome is read off the response.
  const handleProject = useCallback(async (po) => {
    try {
      const result = await projectPO({ id: po.purchase_order_header_id });
      if (result.channel_projection_status === PROJECTION_SYNCED) {
        showToast(t('purchaseOrders.projection.retrySuccess', { poNumber: po.po_number }), 'success');
      } else {
        showToast(t('purchaseOrders.projection.retryStillFailed', { poNumber: po.po_number }), 'error');
      }
    } catch (error) {
      console.error('projectPO:', error.message);
      showToast(translateRequestError(error, t, 'purchaseOrders.projection.retryFailed'), 'error');
    }
  }, [projectPO, t]);

  // GRN → Shopify outcome chip (GoodsReceipt.shopify_sync_status). A status
  // this page has no copy for renders its raw wire value — visible and
  // obviously wrong, rather than dressed up as "not applicable".
  const syncChip = (syncStatus) => {
    const map = {
      synced: { label: t('purchaseOrderDetail.history.synced'), color: 'success' },
      failed: { label: t('purchaseOrderDetail.history.syncFailed'), color: 'error' },
      not_applicable: { label: t('purchaseOrderDetail.history.syncNotApplicable'), color: 'neutral' },
    };
    const cfg = map[syncStatus];
    if (!cfg) return <StatusChip label={syncStatus} color="neutral" />;
    return <StatusChip label={cfg.label} color={cfg.color} />;
  };

  const closeTab = () => replaceOwnTab(PURCHASE_ORDERS_PATH);

  if (loading) {
    return (
      <div className="page-container">
        <div className="tiles-row">
          <Tile width="full" title={t('purchaseOrderDetail.loadingTitle')}>
            <div className="grid-loading-center"><CircularProgress /></div>
          </Tile>
        </div>
      </div>
    );
  }

  // The failure renders where it happened — the tab strip is the way out.
  // Both 404 (PO deleted / bad URL) and 5xx land here.
  if (poError || !poData) {
    return (
      <div className="page-container">
        <div className="tiles-row">
          <Tile width="full" title={t('purchaseOrderDetail.loadingTitle')}>
            <Alert severity="error">
              {poError ? t('purchaseOrderDetail.loadFailed') : t('purchaseOrderDetail.notFound')}
            </Alert>
          </Tile>
        </div>
      </div>
    );
  }

  const meta = [
    { key: 'supplier', label: t('purchaseOrderDetail.meta.supplier'), value: poData.supplier_name || '—' },
    { key: 'location', label: t('purchaseOrderDetail.meta.location'), value: poData.location_name },
    ...(status === 'draft' ? [{
      key: 'expected',
      label: t('purchaseOrderDetail.meta.expectedDeliveryDate'),
      // Starts at the date issuing would set; a cleared field goes back to it.
      value: issueDate.error ?? (
        <AppDatePicker
          value={issuePick ?? issueDate.data?.need_by_date ?? null}
          onChange={(day) => setIssuePick(day === '' ? null : day)}
          minDate={storeToday}
          disabled={!issueDate.data}
        />
      ),
    }] : []),
    ...(canReschedule(poData) ? [{
      key: 'expected',
      label: t('purchaseOrderDetail.meta.expectedDeliveryDate'),
      // An overdue date reads red and cannot be saved back: it is before
      // `minDate` (AppDatePicker's onRefusedChange).
      value: (
        <>
          <AppDatePicker
            value={arrivalEdit ?? poData.need_by_date}
            onChange={setArrivalEdit}
            onRefusedChange={setArrivalRefused}
            minDate={storeToday}
            disabled={rescheduleMutation.isPending}
          />
          {arrivalDirty && (
            <>
              <Button size="small" variant="contained" onClick={saveArrival} loading={rescheduleMutation.isPending} disabled={!arrivalEdit || arrivalRefused}>
                {t('btn.saveChanges')}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setArrivalEdit(null)} disabled={rescheduleMutation.isPending}>
                {t('btn.discardChanges')}
              </Button>
            </>
          )}
        </>
      ),
    }] : isIssued ? [{
      key: 'expected',
      label: t('purchaseOrderDetail.meta.expectedDeliveryDate'),
      value: poData.need_by_date ? (
        // `due_status` is the backend's verdict in the store's timezone
        // (classify_due_status); comparing dates here would paint the PO
        // from the viewer's clock.
        <span style={poData.due_status === 'overdue' ? { color: 'var(--color-error)', fontWeight: 'var(--font-weight-semibold)' } : undefined}>
          {formatDate(poData.need_by_date)}
        </span>
      ) : '—',
    }] : []),
    { key: 'created', label: t('purchaseOrderDetail.meta.created'), value: formatDateTime(poData.created_date) },
    ...(poData.reference_number ? [{ key: 'reference', label: t('purchaseOrderDetail.meta.reference'), value: poData.reference_number }] : []),
    ...(poData.is_erp_managed ? [{ key: 'exact', label: t('purchaseOrderDetail.meta.exactPo'), value: poData.exact_po_number || t('purchaseOrders.erpManaged.exactBadge') }] : []),
    ...(poData.shows_prices ? [{
      key: 'total',
      label: t('purchaseOrderDetail.meta.total'),
      value: poData.total_supplier_currency == null
        ? '—'
        : formatCurrency(poData.total_supplier_currency, poCurrency),
    }] : []),
    ...(poData.cancelled_reason || poData.closed_reason ? [{
      key: 'reason',
      label: t('purchaseOrderDetail.meta.reason'),
      value: poData.cancelled_reason || poData.closed_reason,
    }] : []),
    ...(totalReceived > 0 ? [{
      key: 'progress',
      label: t('purchaseOrderDetail.meta.progress'),
      value: t('purchaseOrderDetail.meta.progressValue', {
        received: formatNumber(totalReceived, 0),
        ordered: formatNumber(totalOrdered, 0),
        remaining: formatNumber(totalOrdered - totalReceived, 0),
      }),
    }] : []),
  ];

  return (
    <div className="page-container">
      <div className="tiles-row">
        <Tile
          width="full"
          title={(
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              {t('purchaseOrderDetail.title', { poNumber: poData.po_number })}
              <PoStatusChip value={poData.status} />
            </span>
          )}
          editActions={hasPending && (
            <>
              <Button variant="contained" onClick={submitReceipt} loading={submitting}>
                {t('purchaseOrderDetail.receive.submit')}
              </Button>
              <Button variant="outlined" onClick={discardReceipt} disabled={submitting}>
                {t('btn.discardChanges')}
              </Button>
            </>
          )}
          headerActions={(
            <>
              {!hasPending && (
                <>
                  {/* A draft's prices are a store choice (Store settings →
                      Purchase orders); the switch sits where the merchant
                      sees its effect — the lines, the Total and the PDF. */}
                  {status === 'draft' && (
                    <Tooltip title={t('purchaseOrderDetail.showPricesHint')} describeChild>
                      <FormControlLabel
                        sx={{ mr: 1 }}
                        control={(
                          <Switch
                            size="small"
                            checked={poData.shows_prices}
                            onChange={handleShowPrices}
                            disabled={showDraftPrices.isPending}
                          />
                        )}
                        label={t('purchaseOrderDetail.showPrices')}
                      />
                    </Tooltip>
                  )}
                  <PurchaseOrderActions po={poData} mayCreateOrders={mayCreateOrders} onAction={handleAction} busy={busy} running={running} />
                </>
              )}
              <Tooltip title={t('purchaseOrderDetail.backToOrders')}>
                <IconButton size="small" onClick={closeTab}>
                  <X size={ICON.xl} />
                </IconButton>
              </Tooltip>
            </>
          )}
        >
          <GridPage>
            <PoStatusBanner value={poData.status} />
            <ProjectionPanel po={poData} onProject={handleProject} />

            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              alignItems: 'center',
              mb: 2,
              p: 1.5,
              backgroundColor: 'var(--color-fill)',
              borderRadius: 1,
              border: '1px solid var(--border-color)',
            }}>
              {meta.map((m) => (
                <FieldLine key={m.key} label={<strong>{m.label}:</strong>}>{m.value}</FieldLine>
              ))}
            </Box>

            {/* The internal note is the merchant's own remark on the order,
                never on the document, so it is editable in every status.
                Supplier notes are the document's and freeze with it. */}
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label={t('purchaseOrderDetail.meta.notes')}
                  value={notesEdit ?? poData.notes}
                  onChange={(e) => setNotesEdit(e.target.value)}
                  size="small"
                  multiline
                  fullWidth
                  disabled={notesMutation.isPending}
                />
                {notesDirty && (
                  <>
                    <Button variant="contained" onClick={saveNotes} loading={notesMutation.isPending}>
                      {t('purchaseOrderDetail.notes.save')}
                    </Button>
                    <Button variant="outlined" onClick={() => setNotesEdit(null)} disabled={notesMutation.isPending}>
                      {t('btn.discardChanges')}
                    </Button>
                  </>
                )}
              </Box>
              {poData.supplier_notes && (
                <Typography variant="body2" color="text.secondary">
                  <strong>{t('purchaseOrderDetail.meta.supplierNotes')}:</strong> {poData.supplier_notes}
                </Typography>
              )}
            </Box>

            <GridViewport>
              {gridTheme && (
                <AgGridReact
                  {...defaultGridProps}
                  {...editableGridProps}
                  context={gridContext}
                  {...gridState}
                  theme={gridTheme}
                  columnDefs={columnDefs}
                  rowData={rowData}
                  defaultColDef={defaultColDef}
                  columnTypes={columnTypes}
                  getRowId={getRowId}
                  getContextMenuItems={copyOnlyContextMenuItems}
                  onCellValueChanged={handleCellValueChanged}
                  pinnedBottomRowData={pinnedBottomRowData}
                />
              )}
            </GridViewport>

            {receiptHistory.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Accordion expanded={historyOpen ?? historyFailed} onChange={(_, open) => setHistoryOpen(open)}>
                  <AccordionSummary expandIcon={<CaretDown size={ICON.xl} />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
                      {t('purchaseOrderDetail.history.title')} ({receiptHistory.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {receiptHistory.map((receipt) => (
                        <Box key={receipt.GoodsReceiptID} sx={{ p: 1.5, border: '1px solid var(--border-color)', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'var(--font-weight-semibold)' }}>{receipt.ReceiptNumber}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {syncChip(receipt.shopify_sync_status)}
                              <Typography variant="caption" color="text.secondary">{formatDate(receipt.ReceiptDate)}</Typography>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('purchaseOrderDetail.history.receivedBy')}: {receipt.created_by_name || '—'}
                            {' | '}
                            {t('purchaseOrderDetail.history.totalQty')}: {receipt.total_received}
                            {receipt.Notes && ` | ${receipt.Notes}`}
                          </Typography>
                          {receipt.shopify_sync_status === 'failed' && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {t('purchaseOrderDetail.history.syncError')}
                            </Typography>
                          )}
                          {receipt.lines?.length > 0 && (
                            <Box sx={{ mt: 1, pl: 1 }}>
                              {receipt.lines.map((line, idx) => (
                                <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {formatItemLabel(line, isShopify)} — {line.QuantityReceived} {t('purchaseOrderDetail.history.units')}
                                </Typography>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
            {historyQuery.error && (
              <Alert severity="error" sx={{ mt: 2 }}>{t('purchaseOrderDetail.history.loadFailed')}</Alert>
            )}
          </GridPage>
        </Tile>
      </div>

      {/* The receipt's confirmation; the lifecycle brings its own. */}
      <ConfirmDialog {...dialogProps} />
      {lifecycleDialogs}
    </div>
  );
}

export default PurchaseOrderDetail;
